import { BACK_TARGET_QUERY, CREATED_ITEM_QUERY } from '@/utils/constants'

// eslint-disable-next-line no-promise-executor-return
export const sleep = ms => new Promise(res => setTimeout(res, ms))

// Internal-only paths to guard against open-redirect via query params.
export const isSafeInternalPath = path =>
  typeof path === 'string' && path.startsWith('/') && !path.startsWith('//')

// Resolves a focus-layout back-navigation target: honors `?from=<path>` when
// safe, otherwise falls back to the provided default (typically set by the
// route meta or the caller).
export const resolveBackTarget = (route, fallback) => {
  const from = route?.query?.[BACK_TARGET_QUERY]
  return isSafeInternalPath(from) ? from : fallback
}

// Pushes to a focus-layout page and attaches `?from=<current path>` so the
// page can return to the originating view (via resolveBackTarget) instead of
// its hardcoded fallback. `target` is either a path string or a location
// object; pre-existing query params on `target` are preserved.
export const pushWithBack = (router, target, route) => {
  const normalized = typeof target === 'string' ? { path: target } : target
  return router.push({
    ...normalized,
    query: { ...(normalized.query || {}), [BACK_TARGET_QUERY]: route.fullPath },
  })
}

// Appends/overwrites one query param on a fullPath string (e.g. a stored
// BACK_TARGET_QUERY target) without disturbing its existing query.
export const pathWithQueryParam = (path, key, value) => {
  const [base, search] = path.split('?')
  const params = new URLSearchParams(search || '')
  params.set(key, value)
  return `${base}?${params.toString()}`
}

// Detour flows (e.g. the intro-item picker) send the user to an add-item page
// with a ?from= back target: return there, carrying the fresh item's id.
// Returns true if it navigated, false when there is no detour to return to.
export const returnToBackTargetWithItem = (router, route, itemId) => {
  const backTarget = resolveBackTarget(route, null)
  if (!backTarget || !itemId) return false

  router.push(pathWithQueryParam(backTarget, CREATED_ITEM_QUERY, itemId))
  return true
}

export function timedPromise(promise, ms) {
  let timer = null

  return Promise.race([
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error('TIMED_OUT')), ms)
    }),
    promise.then(value => {
      clearTimeout(timer)
      return value
    }),
  ])
}

export function debounce(fn, delay) {
  let timerId
  return function abc(...args) {
    if (timerId) clearTimeout(timerId)

    timerId = setTimeout(() => {
      fn.call(this, ...args)
      timerId = null
    }, delay)
  }
}

/**
 * Serializes repeated async triggers into a single running task and
 * optionally coalesces the latest queued arguments while that task runs.
 */
export function createQueuedTask(run, mergeArgs = (_, next) => next) {
  let activePromise = null
  let hasQueuedRun = false
  let queuedArgs

  return async (args) => {
    if (!hasQueuedRun) {
      queuedArgs = args
      hasQueuedRun = true
    } else {
      queuedArgs = mergeArgs(queuedArgs, args)
    }

    if (activePromise) {
      return activePromise
    }

    activePromise = (async () => {
      while (hasQueuedRun) {
        const currentArgs = queuedArgs
        hasQueuedRun = false
        queuedArgs = undefined
        await run(currentArgs)
      }
    })()

    try {
      return await activePromise
    } finally {
      activePromise = null
    }
  }
}

export const throttle = (fn, delay) => {
  let lastCall = 0
  return function abc(...args) {
    const now = new Date().getTime()
    if (now - lastCall < delay) return
    lastCall = now
    // eslint-disable-next-line consistent-return
    return fn.call(this, ...args)
  }
}

export function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export const throttleEnsureLast = (fn, wait) => {
  let previouslyRun
  let queuedToRun

  return function invokeFn(...args) {
    const now = Date.now()

    queuedToRun = clearTimeout(queuedToRun)

    if (!previouslyRun || (now - previouslyRun >= wait)) {
      fn.call(this, ...args)
      previouslyRun = now
    } else {
      queuedToRun = setTimeout(invokeFn.bind(this, ...args), wait - (now - previouslyRun))
    }
  }
}

export function getRouteInheritedMeta(route) {
  let inheritedMeta = {}

  for (const r of route.matched) {
    inheritedMeta = { ...inheritedMeta, ...r.meta }
  }

  return inheritedMeta
}

export async function sha512Encode(text) {
  if (!window.crypto?.subtle) {
    const { default: sha512 } = await import('hash.js/lib/hash/sha/512')
    return sha512().update(text).digest('hex')
  }

  const encoder = new TextEncoder()
  const data = encoder.encode(text)
  const hashBuffer = await crypto.subtle.digest('SHA-512', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
}

export const tryJsonParse = str => {
  try {
    return JSON.parse(str)
  } catch (error) {
    return str
  }
}

export const tryJsonStringify = obj => {
  try {
    return JSON.stringify(obj)
  } catch (error) {
    return obj
  }
}

export const loadUrl = url => new Promise((resolve, reject) => {
  const script = document.createElement('script')
  script.type = 'text/javascript'
  script.src = url
  script.addEventListener('load', () => resolve(script), false)
  script.addEventListener('error', () => reject(script), false)
  document.body.appendChild(script)
})

export const loadUrls = urls => Promise.all(urls.map(loadUrl))

// NOTE caveat: larges files might be problematic, loaded into ram
export const downloadFile = (data, filename) => {
  const url = URL.createObjectURL(new Blob([data]))
  const link = document.createElement('a')
  link.href = url

  link.setAttribute('download', filename)

  document.body.appendChild(link)
  link.click()
  setTimeout(() => link.remove(), 1e3)
}

export function isValidHttpUrl(string) {
  let url
  try {
    url = new URL(string)
  } catch (_) {
    return false
  }

  // only accept http protocols
  if (!['http:', 'https:'].includes(url.protocol)) return false

  // make sure hostname has at least one dot (we support ips too)
  if (!url.hostname.includes('.')) return false

  return true
}

export function getDeviceColorSchemePreference() {
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light'
}

export function getLocationQuery() {
  const { hash, search } = window.location
  if (search) return search

  const searchPos = hash.indexOf('?')
  return searchPos > -1 ? hash.substring(searchPos) : ''
}
